import {
  authorizedFetch,
  API_ORIGIN,
  managedEmployerOrganisationSwitcherEnabled,
  useEmployerOrganisationOptional,
} from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { EmployerRecentApplicantRow } from './use-managed-org-recent-applicants'
import { normalizeApplicantProfileFromApi } from './use-opportunity-applicants'

export type InterviewerNextInterview = {
  id: string
  round_name: string
  scheduled_start: string
  scheduled_end: string
}

type InterviewerCandidatesResponse = {
  applicants: Array<{
    id: string
    opportunity_id: string
    profile: Parameters<typeof normalizeApplicantProfileFromApi>[0]
    applied_at: string
    match_score: number | null
    evaluation_id?: string | null
    next_interview?: InterviewerNextInterview | null
    interviews?: InterviewerNextInterview[]
    live_session_id?: string | null
  }>
}

export type InterviewerCandidateRow = EmployerRecentApplicantRow & {
  evaluation_id?: string | null
  next_interview?: InterviewerNextInterview | null
  interviews?: InterviewerNextInterview[]
  live_session_id?: string | null
}

export const INTERVIEWER_CANDIDATES_QUERY_KEY = ['interviewer-candidates'] as const

export function useInterviewerCandidates() {
  const empOrg = useEmployerOrganisationOptional()
  const switcherOn = managedEmployerOrganisationSwitcherEnabled() && empOrg?.enabled === true
  const orgSlug = switcherOn ? (empOrg?.activeOrganisationSlug ?? null) : null
  const orgsReady = !switcherOn || !empOrg?.organisationsLoading

  return useQuery({
    queryKey: orgSlug
      ? [...INTERVIEWER_CANDIDATES_QUERY_KEY, orgSlug]
      : INTERVIEWER_CANDIDATES_QUERY_KEY,
    enabled: orgsReady,
    queryFn: async (): Promise<InterviewerCandidateRow[]> => {
      const qs = orgSlug ? `?organisation=${encodeURIComponent(orgSlug)}` : ''
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/interviewer/candidates/${qs}`,
      )
      if (!response.ok) {
        throw new Error('Failed to load interview candidates')
      }
      const data = (await response.json()) as InterviewerCandidatesResponse
      return (data.applicants ?? []).map((row) => ({
        id: row.id,
        opportunity_id: row.opportunity_id,
        profile: normalizeApplicantProfileFromApi(row.profile),
        applied_at: row.applied_at,
        match_score: row.match_score,
        evaluation_id: row.evaluation_id ?? null,
        next_interview: row.next_interview ?? null,
        interviews: row.interviews ?? [],
        live_session_id: row.live_session_id ?? null,
      }))
    },
  })
}

export function useScheduleInterview(applicationId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      round_name: string
      scheduled_start: string
      scheduled_end: string
      location_type?: string
      location_details?: string
    }) => {
      const res = await authorizedFetch(`${API_ORIGIN}/api/applications/${applicationId}/interviews/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_type: 'virtual',
          location_details: '',
          ...body,
        }),
      })
      if (!res.ok) throw new Error('Could not schedule the interview')
      return res.json() as Promise<InterviewerNextInterview & { location_type: string }>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INTERVIEWER_CANDIDATES_QUERY_KEY })
    },
  })
}
